import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    cardClickableMixin,
    defaultBorderMixin
} from "@rebasepro/ui";

const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

import { PersonAvatar } from "./_avatar";
import {
    MIN_QUERY,
    RESULTS,
    shortlistFacts,
    shortlistLabel,
    type ShortlistCandidate
} from "./_shortlist";

/**
 * The picker itself. Its rules — what reaches the message and what may not —
 * live in `_shortlist.ts`, where they are covered by tests.
 */
export { shortlistBlock, shortlistLabel, type ShortlistCandidate } from "./_shortlist";

/**
 * What the picker says it is for. The same search serves two acts that expose
 * different things: naming people in an email sends the facts under each name
 * and nothing else, while presenting them opens their whole profile to the
 * company — which the presentation panel states before its own button.
 */
const INTRO = {
    email: "Elige a quién nombrar. Al mensaje va sólo el nombre, lo que estudió, dónde está y sus años de experiencia — nunca su email, su teléfono ni su CV.",
    presentation: "Elige a quién presentar. Debajo escribes qué le dices a la empresa de cada persona."
} as const;

export function CandidateShortlist({
    client,
    selected,
    onChange,
    purpose = "email"
}: {
    client: any;
    selected: ShortlistCandidate[];
    onChange: (next: ShortlistCandidate[]) => void;
    purpose?: keyof typeof INTRO;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<ShortlistCandidate[]>([]);
    const [searching, setSearching] = useState(false);
    /**
     * Which of the two searches failed, if any. A failed search is not an empty
     * one: "nadie" printed over a query that never ran is how a recruiter
     * concludes the pool has nobody and stops looking.
     */
    const [failed, setFailed] = useState<{ byName: boolean; byProfile: boolean }>({ byName: false, byProfile: false });

    const data = client?.data;
    const chosen = useMemo(() => new Set(selected.map(c => c.id)), [selected]);

    useEffect(() => {
        const term = query.trim();
        if (!data || term.length < MIN_QUERY) {
            setResults([]);
            setFailed({ byName: false, byProfile: false });
            return;
        }
        let live = true;
        setSearching(true);
        // Debounced for the same reason the thread picker is: a request per
        // keystroke against three hundred rows, and the answer only matters once
        // somebody stops typing.
        const timer = setTimeout(async () => {
            try {
                // Two searches, because each misses what the other finds.
                //
                // The name as typed, as a substring: "Fern" finds Fernández
                // while it is still being typed, which a full-text index never
                // will. And the collection's own full-text search (the `search`
                // block in collections/talents.ts): degree, other studies,
                // certifications, skills, their objective and their city, in
                // Spanish, accent-blind and forgiving of "iso14001" — which is
                // what somebody looking for "auditor ISO 14001 en Bogotá" is
                // actually typing. It was `headline ilike` here once, over a
                // column the table does not have, and matched nothing.
                const [byName, byProfile] = await Promise.allSettled([
                    data.collection("talents").find({ where: { full_name: ["ilike", `%${term}%`] }, limit: RESULTS }),
                    data.collection("talents").find({ searchString: term, limit: RESULTS })
                ]);
                if (!live) return;
                const seen = new Map<string, ShortlistCandidate>();
                // Name matches first: somebody typing a name wants that person.
                for (const settled of [byName, byProfile]) {
                    if (settled.status !== "fulfilled") continue;
                    for (const row of ((settled.value?.data ?? []) as ShortlistCandidate[])) {
                        if (row?.id && !seen.has(String(row.id))) seen.set(String(row.id), { ...row, id: String(row.id) });
                    }
                }
                setResults([...seen.values()].slice(0, RESULTS));
                setFailed({ byName: byName.status === "rejected", byProfile: byProfile.status === "rejected" });
            } finally {
                if (live) setSearching(false);
            }
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [data, query]);

    const toggle = useCallback((candidate: ShortlistCandidate) => {
        onChange(
            chosen.has(candidate.id)
                ? selected.filter(c => c.id !== candidate.id)
                : [...selected, candidate]
        );
    }, [chosen, selected, onChange]);

    return (
        <div className={cls("flex flex-col gap-2 rounded-md p-3 border", defaultBorderMixin)}>
            <Typography variant="body2" color="secondary">{INTRO[purpose]}</Typography>

            {selected.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                    {selected.map(candidate => (
                        <Chip
                            key={candidate.id}
                            size="small"
                            colorScheme="greenLighter"
                            onClick={() => toggle(candidate)}
                        >
                            {shortlistLabel(candidate)} ✕
                        </Chip>
                    ))}
                </div>
            )}

            <TextField
                size="small"
                label="Buscar por nombre, estudios, certificación, habilidad o ciudad"
                value={query}
                onChange={(e: any) => setQuery(e.target.value)}
            />

            {searching && <Typography variant="body2" color="secondary">Buscando…</Typography>}

            {!searching && query.trim().length >= MIN_QUERY && failed.byName && failed.byProfile && (
                <Typography variant="body2" color="error">
                    No se pudo buscar. No es que no haya nadie: la búsqueda falló.
                </Typography>
            )}

            {!searching && query.trim().length >= MIN_QUERY && failed.byName !== failed.byProfile && (
                <Typography variant="body2" color="secondary">
                    {failed.byProfile
                        ? "Sólo se ha buscado por nombre: la búsqueda por estudios, certificaciones y ciudad falló."
                        : "Sólo se ha buscado por estudios, certificaciones y ciudad: la búsqueda por nombre falló."}
                </Typography>
            )}

            {!searching && query.trim().length >= MIN_QUERY && results.length === 0 && !(failed.byName && failed.byProfile) && (
                <Typography variant="body2" color="secondary">
                    Nadie con ese nombre, ni con esos estudios, certificación, habilidad o ciudad.
                </Typography>
            )}

            <div className="flex flex-col gap-1">
                {results.map(candidate => (
                    <Card
                        key={candidate.id}
                        onClick={() => toggle(candidate)}
                        className={cls(
                            "p-2 flex items-center gap-2.5",
                            cardClickableMixin,
                            chosen.has(candidate.id) && "opacity-50"
                        )}
                    >
                        <PersonAvatar
                            name={shortlistLabel(candidate)}
                            photoKey={candidate.photo_url}
                            size={28}
                        />
                        <div className="flex flex-col min-w-0 grow">
                            <Typography variant="body2" className="truncate">
                                {shortlistLabel(candidate)}
                                {chosen.has(candidate.id) && " · ya está"}
                            </Typography>
                            <Typography variant="body2" color="secondary" className="truncate">
                                {shortlistFacts(candidate).join(" · ") || "sin estudios, lugar ni experiencia en su perfil"}
                            </Typography>
                        </div>
                    </Card>
                ))}
            </div>

            {selected.length > 0 && (
                <div>
                    <Button size="small" variant="outlined" onClick={() => onChange([])}>
                        Quitar todos
                    </Button>
                </div>
            )}
        </div>
    );
}
